import { useState, useEffect } from "react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import VideoPlayer from "@/components/VideoPlayer";
import AddToLibraryDialog from "@/components/AddToLibraryDialog";
import { ScopeBadge } from "@/components/ScopeBadge";
import { RichTextView } from "@/components/RichTextEditor";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { CATEGORY_COLORS, BELT_COLORS } from "@/lib/constants";
import { ArrowLeft, BookmarkPlus } from "lucide-react";

// Shows the same details as the Techniques detail page, inside a dialog.
// Fetches the latest technique data by id (falls back to the node snapshot).
export default function TechniqueDetailDialog({ open, onOpenChange, technique }) {
  const { user } = useAuth();
  const [t, setT] = useState(technique);
  const [addOpen, setAddOpen] = useState(false);

  useEffect(() => {
    setT(technique);
    if (open && technique?.id) {
      api.get(`/techniques/${technique.id}`).then(({ data }) => setT(data)).catch(() => {});
    }
  }, [open, technique]);

  if (!t) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-card border-border max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="technique-detail-dialog">
        <Button data-testid="dialog-back-to-sequence" variant="ghost" onClick={() => onOpenChange(false)}
          className="text-muted-foreground hover:text-white -ml-2 w-fit">
          <ArrowLeft className="h-4 w-4 mr-1.5" /> Back to sequence
        </Button>

        <VideoPlayer technique={t} />

        <div className="flex items-center gap-2 flex-wrap mt-2">
          {t.category && <span className="text-xs font-semibold px-2.5 py-1 rounded" style={{ background: `${CATEGORY_COLORS[t.category]}22`, color: CATEGORY_COLORS[t.category] }}>{t.category}</span>}
          {t.position && <span className="text-xs px-2.5 py-1 rounded bg-secondary text-secondary-foreground">{t.position}</span>}
          {t.belt_level && <span className="text-xs px-2.5 py-1 rounded border border-border flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full" style={{ background: BELT_COLORS[t.belt_level] }} />{t.belt_level} belt</span>}
          <ScopeBadge scope={t.scope} className="border border-border text-muted-foreground text-xs px-2.5 py-1" />
        </div>

        <h2 className="font-heading font-bold text-2xl sm:text-3xl tracking-tight">{t.name}</h2>
        {t.owner_name && <p className="text-sm text-muted-foreground -mt-1">Added by {t.owner_name}</p>}

        {t.scope === "platform" && user?.role !== "platform_admin" && (
          <Button data-testid="dialog-add-to-library-btn" onClick={() => setAddOpen(true)}
            className="bg-primary hover:bg-primary/90 text-white w-fit">
            <BookmarkPlus className="h-4 w-4 mr-1.5" /> Add to Library
          </Button>
        )}

        {t.description && (
          <div>
            <h3 className="text-xs uppercase tracking-[0.2em] text-primary font-semibold mb-2">Details</h3>
            <RichTextView html={t.description} />
          </div>
        )}

        {t.tags?.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {t.tags.map((tag) => <span key={tag} className="text-xs px-2 py-1 rounded bg-secondary text-secondary-foreground">#{tag}</span>)}
          </div>
        )}
      </DialogContent>
      <AddToLibraryDialog open={addOpen} onOpenChange={setAddOpen} item={t} kind="technique" />
    </Dialog>
  );
}
